import * as Sentry from '@sentry/react';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import type { APIError, SearchImagesQuery } from '@vizit/api';
import { useGetBatchStatusQuery } from '@vizit/api';
import type { ImageView } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import * as React from 'react';

interface UseGetImageGenBatchStatusOptions {
  /**
   * The asset/image ID.
   */
  assetId: string;
  /**
   * The batch ID to monitor. If null, the hook will not poll for status.
   */
  batchId: string | null;
  /**
   * The query key to invalidate when batch completes successfully.
   */
  sparkImagesQueryKey: unknown[];
  /**
   * Callback when batch ID should be cleared (on completion, failure, or error).
   */
  onBatchComplete: () => void;
}

interface UseGetImageGenBatchStatusReturn {
  /**
   * Whether image generation is currently in progress.
   */
  isImageGenerationInProgress: boolean;
  /**
   * Whether the batch status query is loading.
   */
  isLoading: boolean;
  /**
   * The current batch status.
   */
  status: string | undefined;
  /**
   * Whether the batch has a generation error (persists after batch ID is cleared).
   */
  hasGenerationError: boolean;
  /**
   * The error message from the batch status (persists after batch ID is cleared).
   */
  generationErrorMessage: string | undefined;
  /**
   * Clears the persisted error state.
   */
  clearError: () => void;
}

/**
 * Hook to monitor the status of an image generation batch.
 * Automatically polls for status updates and handles completion/error states.
 *
 * @param options - Configuration options
 * @returns Batch status information
 */
export const useGetImageGenBatchStatus = (
  options: UseGetImageGenBatchStatusOptions,
): UseGetImageGenBatchStatusReturn => {
  const { assetId, batchId, sparkImagesQueryKey, onBatchComplete } = options;

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Store error state separately so it persists after batch ID is cleared
  const [errorState, setErrorState] = React.useState<{
    hasError: boolean;
    message: string | undefined;
  }>({ hasError: false, message: undefined });

  React.useEffect(() => {
    if (batchId) {
      setErrorState({ hasError: false, message: undefined });
    }
  }, [batchId]);

  const batchStatusQuery = useGetBatchStatusQuery(
    {
      image_id: assetId,
      batch_id: batchId || '',
    },
    {
      enabled: !!batchId,
    },
  );

  const placeholderImages: Partial<ImageView>[] = Array.from({ length: 7 }, (_, index) => ({
    id: `placeholder-${Date.now()}-${index}`,
    name: 'Generating...',
    url: null,
    vizit_score: null,
    omit_score: false,
    parent_id: assetId,
    source: 'generated',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    portfolio_id: '',
    organization_id: '',
  }));

  React.useEffect(() => {
    if (!batchId) return;

    const status = batchStatusQuery.data?.status;
    const message = batchStatusQuery.data?.message;

    Sentry.addBreadcrumb({
      message: 'Image generation batch status update',
      level: 'info',
      data: {
        batch_id: batchId,
        status,
        asset_id: assetId,
      },
    });

    if (status === 'COMPLETED') {
      Sentry.addBreadcrumb({
        message: 'Image generation batch completed',
        level: 'info',
        data: { batch_id: batchId, asset_id: assetId },
      });

      queryClient.setQueryData(
        sparkImagesQueryKey,
        (cachedData: InfiniteData<SearchImagesQuery, unknown> | undefined) => {
          if (!cachedData) {
            return {
              pages: [
                {
                  data: placeholderImages,
                  total_count: 7,
                },
              ],
              pageParams: [undefined],
            };
          }

          const updatedPages = cachedData.pages ? [...cachedData.pages] : [];
          if (updatedPages.length > 0) {
            // Prepend placeholders to first page
            updatedPages[0] = {
              ...updatedPages[0],
              data: [...(placeholderImages as ImageView[]), ...updatedPages[0].data],
              total_count: updatedPages[0].total_count + 7,
            };
          } else {
            // If no pages exist, create the first page with placeholders
            updatedPages.push({
              data: placeholderImages as ImageView[],
              total_count: 7,
            });
          }

          return {
            ...cachedData,
            pages: updatedPages,
          };
        },
      );

      // After a short delay, invalidate to fetch real data
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: sparkImagesQueryKey });
      }, 100);

      onBatchComplete();
    } else if (status === 'FAILED') {
      Sentry.captureException(new Error('Image generation batch failed'), {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Asset,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetImageGeneration,
        },
        extra: { batch_id: batchId, asset_id: assetId, message },
      });

      setErrorState({ hasError: true, message });
      addToast({
        title: 'Image generation failed',
        description: message || 'Failed to generate images. Please try again.',
        level: AlertLevel.Error,
      });

      onBatchComplete();
    }
  }, [
    batchStatusQuery.data?.status,
    batchStatusQuery.data?.message,
    queryClient,
    sparkImagesQueryKey,
    addToast,
    batchId,
    onBatchComplete,
    placeholderImages,
    assetId,
  ]);

  // Handle batch query errors
  React.useEffect(() => {
    if (!batchId) return;

    if (batchStatusQuery.error) {
      const errorMessage = (batchStatusQuery.error as APIError).message || 'An error occurred while generating images.';

      Sentry.captureException(batchStatusQuery.error, {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Asset,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetImageGeneration,
        },
        extra: {
          batch_id: batchId,
          asset_id: assetId,
          error_message: errorMessage,
        },
      });

      // Store error state before clearing batch ID
      setErrorState({ hasError: true, message: errorMessage });
      addToast({
        title: 'Image generation error',
        description: errorMessage,
        level: AlertLevel.Error,
      });

      onBatchComplete();
    }
  }, [batchStatusQuery.error, addToast, batchId, onBatchComplete, assetId]);

  const clearError = React.useCallback(() => {
    setErrorState({ hasError: false, message: undefined });
  }, []);

  return {
    isImageGenerationInProgress: batchStatusQuery.data?.status === 'PROCESSING',
    isLoading: batchStatusQuery.isLoading,
    status: batchStatusQuery.data?.status,
    hasGenerationError: errorState.hasError,
    generationErrorMessage: errorState.message,
    clearError,
  };
};
